import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/ui-brand";
import { GoogleButton } from "@/components/GoogleButton";
import { EgresadoDisclaimer } from "@/components/EgresadoDisclaimer";
import { TermsCheckbox } from "@/components/TermsCheckbox";
import { checks, collect } from "@/lib/validation";
import { RubroSelect, rubroError } from "@/components/RubroSelect";

import { Field } from "@/components/panel";

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginToken, user } = useAuth();
  const params = new URLSearchParams(location.search);
  const isRegister = location.pathname === "/registro";
  const tipo = params.get("tipo") === "empresa" ? "empresa" : "egresado";
  const [mode, setMode] = useState(isRegister ? "register" : "login");
  const [form, setForm] = useState({ marca: "Córdoba Crea Futuro" });
  const [acepta, setAcepta] = useState(false);
  const [errors, setErrors] = useState({});
  const [cat, setCat] = useState({ rubros: [], rubro_otro: "" });
  const clearErr = (k) => setErrors(({ [k]: _omit, ...rest }) => rest);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); clearErr(k); };
  const toggleTerms = (v) => { setAcepta(v); clearErr("terminos"); };

  useEffect(() => { setMode(isRegister ? "register" : "login"); setErrors({}); }, [isRegister]);
  useEffect(() => {
    if (isRegister && tipo === "empresa") {
      api.get("/catalogs").then((r) => setCat(r.data)).catch(() => toast.error("No se pudo cargar la lista de industrias"));
    }
  }, [isRegister, tipo]);

  const dest = (rol) => (rol === "admin" || rol === "super_admin" ? "/admin" : rol === "empresa" ? "/empresa" : "/egresado");

  // Redirección declarativa: navega SOLO cuando `user` quedó commiteado en el contexto.
  // Evita la carrera entre setUser (checkAuth) y un navigate imperativo, que dejaba al
  // usuario en /ingresar tras un login exitoso.
  useEffect(() => {
    if (user?.rol) navigate(dest(user.rol), { replace: true });
  }, [user, navigate]);

  const validar = () => {
    if (mode === "forgot") return collect({ email: checks.email(form.email) });
    if (mode === "login") return collect({ email: checks.email(form.email), password: checks.required(form.password, "Ingresá tu contraseña") });
    const porTipo = tipo === "egresado"
      ? { name: checks.required(form.name, "Ingresá tu nombre y apellido") }
      : { razon_social: checks.required(form.razon_social, "Ingresá la razón social"),
          rubro: rubroError(form.rubro, form.rubro_detalle, cat.rubro_otro),
          contacto_nombre: checks.required(form.contacto_nombre, "Ingresá el nombre de contacto") };
    return collect({ ...porTipo, email: checks.email(form.email), password: checks.password(form.password),
      terminos: acepta ? null : "Para crear tu cuenta tenés que aceptar los Términos y Condiciones de uso" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validar();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(Object.values(errs)[0]);
      return;
    }
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Si el email está registrado, te enviamos un enlace para restablecer tu contraseña.");
        setMode("login");
        return;
      }
      let res;
      if (mode === "login") {
        res = await api.post("/auth/login", { email: form.email, password: form.password });
      } else if (tipo === "egresado") {
        res = await api.post("/auth/register/egresado", { email: form.email, password: form.password, name: form.name, ubicacion: form.ubicacion || "", marca: form.marca, acepta_terminos: acepta });
      } else {
        res = await api.post("/auth/register/empresa", {
          email: form.email, password: form.password, razon_social: form.razon_social, cuit: form.cuit || "", rubro: form.rubro, rubro_detalle: form.rubro_detalle || "",
          contacto_nombre: form.contacto_nombre, contacto_email: form.contacto_email || form.email, contacto_telefono: form.contacto_telefono || "", marca: form.marca, acepta_terminos: acepta,
        });
      }
      if (res.data.mfa_required) { navigate("/verificacion", { state: res.data }); return; }
      await loginToken(res.data.access_token, res.data.refresh_token);
      toast.success("¡Bienvenido/a!");
      // La redirección la dispara el useEffect cuando `user` queda commiteado (sin carrera).
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    }
  };

  return (
    <Section className="py-14 max-w-md">
      <div className="card-brand p-7 sm:p-8">
        <h1 className="h-section text-2xl mb-1">{mode === "login" ? "Ingresá a tu cuenta" : mode === "forgot" ? "Restablecer contraseña" : "Creá tu cuenta"}</h1>
        <p className="text-sm text-[var(--brand-text-muted)] mb-6">{mode === "forgot" ? "Te enviamos un enlace por email para crear una nueva." : "Comunidad de talento Córdoba Crea Futuro"}</p>

        {mode === "register" && (
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--brand-accent)] mb-4" data-testid="reg-tipo-label">
            {tipo === "empresa" ? "Registro de empresa" : "Registro de egresado/a"}
          </p>
        )}

        {mode === "register" && tipo === "egresado" && <EgresadoDisclaimer />}

        <form onSubmit={submit} noValidate className="space-y-3">
          {mode === "register" && tipo === "egresado" && (
            <>
              <Field label="Nombre y apellido" error={errors.name} value={form.name || ""} onChange={set("name")} data-testid="reg-name" />
              <Field label="Ubicación" value={form.ubicacion || ""} onChange={set("ubicacion")} placeholder="Córdoba Capital" data-testid="reg-ubicacion" />
            </>
          )}
          {mode === "register" && tipo === "empresa" && (
            <>
              <Field label="Razón social" error={errors.razon_social} value={form.razon_social || ""} onChange={set("razon_social")} data-testid="reg-razon" />
              <Field label="CUIT" value={form.cuit || ""} onChange={set("cuit")} placeholder="30-12345678-9" data-testid="reg-cuit" />
              <RubroSelect rubros={cat.rubros} otro={cat.rubro_otro} value={form.rubro} detalle={form.rubro_detalle} error={errors.rubro}
                onChange={(v) => { setForm({ ...form, ...v }); clearErr("rubro"); }} testid="reg-rubro" />
              <Field label="Nombre de contacto" error={errors.contacto_nombre} value={form.contacto_nombre || ""} onChange={set("contacto_nombre")} data-testid="reg-contacto-nombre" />
              <Field label="Teléfono de contacto" value={form.contacto_telefono || ""} onChange={set("contacto_telefono")} data-testid="reg-contacto-tel" />
            </>
          )}
          <Field label="Email" type="email" error={errors.email} value={form.email || ""} onChange={set("email")} data-testid="auth-email" />
          {mode !== "forgot" && (
            <Field label="Contraseña" type="password" error={errors.password} value={form.password || ""} onChange={set("password")} data-testid="auth-password" />
          )}
          {mode === "login" && (
            <button type="button" onClick={() => setMode("forgot")} className="text-xs font-semibold text-[var(--brand-accent)]" data-testid="forgot-password-link">¿Olvidaste tu contraseña?</button>
          )}
          {mode === "register" && <TermsCheckbox checked={acepta} onChange={toggleTerms} error={errors.terminos} testid="reg-terms" />}
          <button type="submit" className="btn-primary w-full" data-testid="auth-submit">{mode === "login" ? "Ingresar" : mode === "forgot" ? "Enviar enlace" : "Crear cuenta"}</button>
        </form>
        {(mode === "login" || (mode === "register" && tipo === "egresado")) && <GoogleButton disclaimer={mode === "login"} />}

        {mode === "forgot" ? (
          <p className="text-center text-sm text-[var(--brand-text-muted)] mt-5">
            <button onClick={() => setMode("login")} className="font-bold text-[var(--brand-accent)]" data-testid="back-to-login">Volver a ingresar</button>
          </p>
        ) : (
        <>
        <p className="text-center text-sm text-[var(--brand-text-muted)] mt-5">
          {mode === "login" ? "¿No tenés cuenta? " : "¿Ya tenés cuenta? "}
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold text-[var(--brand-accent)]" data-testid="toggle-mode">
            {mode === "login" ? "Registrate" : "Ingresá"}
          </button>
        </p>
        </>
        )}
      </div>
    </Section>
  );
}
